/**
 * @file CustomerEditDialog.tsx
 * @description Создание / редактирование клиента (phase08.5 shadcn;
 * phase19.31 — нативные списки заменены общим `SelectField`, блок «Источник
 * привлечения» вынесен в `CustomerSourceFields`).
 *
 * E-mail обязателен (customers.md F15, решение пользователя 2026-09-21):
 * без адреса клиент не получит согласование доставки и письма по заказу, а
 * менеджер упрётся в заблокированную кнопку уже на форме заказа. Правило и
 * тексты — в `lib/customer-email.ts`, чтобы диалог и инлайн-поле в заказе
 * ругались одинаково.
 */

import { useEffect, useState } from "react";

import { CustomerSourceFields, type CustomerSourceValue } from "./CustomerSourceFields";
import { LegalEntityAutocomplete } from "./LegalEntityAutocomplete";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { SelectField } from "../ui/SelectField";
import { ApiError } from "../../lib/api-client";
import { customers, type CreateCustomerPayload, type CustomerDto, type CustomerType } from "../../lib/api-customers";
import { CUSTOMER_EMAIL_HINT, validateCustomerEmail } from "../../lib/customer-email";
import { normalizePhoneInput } from "../../lib/phone-format";

interface Props {
  open: boolean;
  customer: CustomerDto | null;
  onClose: () => void;
  onSaved: (c: CustomerDto) => void;
}

/** Вид клиента — список выбора. */
const TYPE_OPTIONS = [
  { value: "INDIVIDUAL", label: "Физлицо" },
  { value: "LEGAL", label: "Юрлицо" },
];

const EMPTY_SOURCE: CustomerSourceValue = { acquisitionSource: "", acquisitionErid: "" };

export function CustomerEditDialog({ open, customer, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [type, setType] = useState<CustomerType>("INDIVIDUAL");
  const [legalEntityId, setLegalEntityId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState<CustomerSourceValue>(EMPTY_SOURCE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setType(customer?.type ?? "INDIVIDUAL");
    setLegalEntityId(customer?.legalEntityId ?? null);
    setDisplayName(customer?.displayName ?? "");
    setEmail(customer?.email ?? "");
    setPhone(customer?.phone ?? "");
    setSource({
      acquisitionSource: customer?.acquisitionSource ?? "",
      acquisitionErid: customer?.acquisitionErid ?? "",
    });
  }, [customer]);

  async function onSubmit() {
    setError(null);
    // F15: адрес обязателен и проверяется по формату — телефон его больше
    // не заменяет.
    const emailProblem = validateCustomerEmail(email);
    if (emailProblem) {
      setError(emailProblem);
      return;
    }
    if (type === "LEGAL" && !legalEntityId) {
      setError("Для юрлица выберите контрагента.");
      return;
    }
    setSaving(true);
    try {
      const payload: CreateCustomerPayload = {
        type,
        legalEntityId: type === "LEGAL" ? legalEntityId ?? undefined : undefined,
        displayName: displayName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        acquisitionSource: source.acquisitionSource || undefined,
        acquisitionErid: source.acquisitionErid.trim() || undefined,
      };
      const saved = customer
        ? await customers.update(customer.id, {
            ...payload,
            legalEntityId: type === "LEGAL" ? legalEntityId : null,
          })
        : await customers.create(payload);
      notify.success(customer ? "Сохранено" : "Клиент создан");
      onSaved(saved);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Не удалось сохранить";
      setError(msg);
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{customer ? "Редактировать клиента" : "Новый клиент"}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 overflow-y-auto">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-type">Тип</Label>
            <SelectField
              id="c-type"
              ariaLabel="Тип клиента"
              value={type}
              onChange={(value) => setType(value as CustomerType)}
              options={TYPE_OPTIONS}
            />
          </div>

          {type === "LEGAL" ? (
            <div className="flex flex-col gap-1.5">
              <Label>Юрлицо</Label>
              <LegalEntityAutocomplete value={legalEntityId} onChange={setLegalEntityId} />
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-name">ФИО / Контактное лицо</Label>
            <Input id="c-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-email">E-mail *</Label>
            <Input
              id="c-email"
              type="email"
              required
              value={email}
              autoComplete="email"
              aria-invalid={email.trim() && validateCustomerEmail(email) ? true : undefined}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="type-caption text-on-surface-variant">{CUSTOMER_EMAIL_HINT}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-phone">Телефон</Label>
            <Input
              id="c-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => setPhone((v) => (v.trim() ? normalizePhoneInput(v) : v))}
              placeholder="+7 912 345-67-89"
            />
            <p className="type-caption text-on-surface-variant">
              Хранится как +79123456789, показывается как +7 912 345-67-89; «8 912…» приведём сами
            </p>
          </div>

          <CustomerSourceFields value={source} onChange={(patch) => setSource((prev) => ({ ...prev, ...patch }))} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} loading={saving}>
            {customer ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
